import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  Chip,
  Stack,
  Divider,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Collapse,
  InputAdornment,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  UploadFile as UploadIcon,
  Refresh as RefreshIcon,
  AssignmentReturn as ReturnIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  LocalShipping as RtoIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  importMeeshoReturnReason,
  getMeeshoReturnReasonList,
  getMeeshoReturnReasonExport,
  getMeeshoReturnReasonAnalysis,
  getMeeshoReturnReasonFilters,
  clearImportResult,
} from "app/redux/slice/meeshoReturnReasonSlice";

// ── Constants ──────────────────────────────────────────────────────────────

const MEESHO_PINK = "#F43397";
const CHART_COLORS = [
  "#F43397",
  "#9C27B0",
  "#FF9800",
  "#3F51B5",
  "#009688",
  "#F44336",
  "#4CAF50",
  "#2196F3",
  "#FF5722",
  "#607D8B",
];

const REASON_PALETTE = {
  "Have size / fit related issues": { color: "#D32F2F", bg: "#FFEBEE" },
  "Have other quality related issues": { color: "#E65100", bg: "#FFF3E0" },
  "Don't need the product anymore": { color: "#6A1B9A", bg: "#F3E5F5" },
  "Received wrong product (different color / size / product)": {
    color: "#1565C0",
    bg: "#E3F2FD",
  },
  "Received defective product": { color: "#4E342E", bg: "#EFEBE9" },
};

const getReasonStyle = (reason) =>
  REASON_PALETTE[reason] || { color: "#37474F", bg: "#ECEFF1" };

const fmtDate = (v) => (v ? moment(v).format("DD/MM/YYYY") : "—");

// ── Sub-components ─────────────────────────────────────────────────────────

/** Full-text reason badge — wraps naturally, no truncation */
const ReasonBadge = ({ reason }) => {
  if (!reason)
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );
  const { color, bg } = getReasonStyle(reason);
  return (
    <Box
      sx={{
        display: "inline-block",
        px: 1,
        py: 0.25,
        borderRadius: "4px",
        bgcolor: bg,
        color,
        fontWeight: 600,
        fontSize: "0.78rem",
        lineHeight: 1.5,
        wordBreak: "break-word",
        whiteSpace: "normal",
      }}
    >
      {reason}
    </Box>
  );
};

/** Compact chip for the raw-data table — truncated with tooltip */
const ReasonChip = ({ reason }) => {
  if (!reason)
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );
  const { color, bg } = getReasonStyle(reason);
  const short = reason.length > 26 ? reason.slice(0, 26) + "…" : reason;
  return (
    <Tooltip title={reason} placement="top-start" arrow>
      <Chip
        label={short}
        size="small"
        sx={{
          bgcolor: bg,
          color,
          fontWeight: 600,
          fontSize: "0.72rem",
          border: `1px solid ${alpha(color, 0.25)}`,
          cursor: "default",
        }}
      />
    </Tooltip>
  );
};

const SummaryCard = ({ label, value, sub, color, icon }) => (
  <Card elevation={2} sx={{ height: "100%", borderTop: `3px solid ${color}` }}>
    <CardContent sx={{ p: 2.5 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontWeight: 600, mb: 0.5 }}
          >
            {label}
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color, lineHeight: 1 }}
          >
            {value}
          </Typography>
          {sub && (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", mt: 0.5, display: "block" }}
            >
              {sub}
            </Typography>
          )}
        </Box>
        <Box sx={{ color, opacity: 0.18, fontSize: 44, flexShrink: 0, ml: 1 }}>
          {icon}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const ImportResultBanner = ({ result }) => {
  if (!result) return null;
  const severity =
    result.status === "completed"
      ? "success"
      : result.status === "partial"
      ? "warning"
      : "error";
  return (
    <Alert severity={severity} icon={<CheckCircleIcon />} sx={{ mt: 2, mb: 1 }}>
      <strong>{result.fileName}</strong> — {result.inserted} new &nbsp;·&nbsp;
      {result.updated} updated &nbsp;·&nbsp;
      {result.withinFileDuplicates} in-file duplicates &nbsp;·&nbsp;
      {result.skipped} skipped &nbsp;(total rows: {result.totalRows})
    </Alert>
  );
};

// Bar chart label shown to the right of each bar
const BarValueLabel = ({ x, y, width, value }) => {
  if (!value) return null;
  return (
    <text
      x={x + width + 6}
      y={y + 4}
      fill="#666"
      textAnchor="start"
      fontSize={11}
      dominantBaseline="middle"
    >
      {value}
    </text>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────

const MeeshoReturnReasonAnalysis = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);

  const { analysis, list, filters, importResult } = useSelector(
    (s) => s.meeshoReturnReason,
  );

  const [tab, setTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selCategory, setSelCategory] = useState("");
  const [selSku, setSelSku] = useState("");
  const [selReason, setSelReason] = useState("");
  const [isRTO, setIsRTO] = useState("");
  const [showFilters, setShowFilters] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [expandedSkus, setExpandedSkus] = useState(new Set());

  const analysisParams = useCallback(() => {
    const p = {};
    if (fromDate) p.fromDate = fromDate;
    if (toDate) p.toDate = toDate;
    if (selCategory) p.category = selCategory;
    if (selSku) p.sku = selSku;
    if (selReason) p.returnReason = selReason;
    if (isRTO !== "") p.isRTO = isRTO;
    return p;
  }, [fromDate, toDate, selCategory, selSku, selReason, isRTO]);

  const listParams = useCallback(
    () => ({
      page,
      perPage,
      ...(search && { search }),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      ...(selCategory && { category: selCategory }),
      ...(selSku && { sku: selSku }),
      ...(selReason && { returnReason: selReason }),
      ...(isRTO !== "" && { isRTO }),
    }),
    [
      page,
      perPage,
      search,
      fromDate,
      toDate,
      selCategory,
      selSku,
      selReason,
      isRTO,
    ],
  );

  useEffect(() => {
    dispatch(getMeeshoReturnReasonFilters());
    dispatch(getMeeshoReturnReasonAnalysis({}));
  }, [dispatch]);

  useEffect(() => {
    if (tab === 1) dispatch(getMeeshoReturnReasonList(listParams()));
  }, [dispatch, tab, listParams]);

  const handleApply = () => {
    setPage(1);
    dispatch(getMeeshoReturnReasonAnalysis(analysisParams()));
    if (tab === 1)
      dispatch(getMeeshoReturnReasonList({ ...listParams(), page: 1 }));
  };

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setSelCategory("");
    setSelSku("");
    setSelReason("");
    setIsRTO("");
    setSearch("");
    setPage(1);
    dispatch(getMeeshoReturnReasonAnalysis({}));
    dispatch(getMeeshoReturnReasonList({ page: 1, perPage }));
  };

  const handlePerPageChange = (e) => {
    const newPerPage = +e.target.value;
    setPerPage(newPerPage);
    setPage(1);
    dispatch(
      getMeeshoReturnReasonList({
        ...listParams(),
        page: 1,
        perPage: newPerPage,
      }),
    );
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".csv")) {
      enqueueSnackbar("Please upload a CSV file", { variant: "error" });
      return;
    }
    setUploading(true);
    dispatch(clearImportResult());
    const formData = new FormData();
    formData.append("file", file);
    const result = await dispatch(importMeeshoReturnReason(formData)).unwrap();
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (
      result?.data?.data?.status === "completed" ||
      result?.data?.data?.status === "partial"
    ) {
      enqueueSnackbar("Import successful", { variant: "success" });
      dispatch(getMeeshoReturnReasonAnalysis(analysisParams()));
      dispatch(getMeeshoReturnReasonFilters());
    } else {
      enqueueSnackbar("Import failed. Check the file format.", {
        variant: "error",
      });
    }
  };

  const toggleSkuExpand = (sku) =>
    setExpandedSkus((prev) => {
      const next = new Set(prev);
      next.has(sku) ? next.delete(sku) : next.add(sku);
      return next;
    });

  const buildAndDownloadCsv = (rows) => {
    const headers = [
      "Suborder Number",
      "Product Name",
      "SKU",
      "Variation",
      "Category",
      "Qty",
      "Type of Return",
      "Return Reason",
      "Detailed Reason",
      "Return Created Date",
      "Delivered Date",
      "Courier Partner",
      "AWB Number",
    ];
    const csvContent = [
      headers.join(","),
      ...rows.map((r) =>
        [
          r.suborderNumber,
          `"${(r.productName || "").replace(/"/g, '""')}"`,
          r.sku,
          r.variation,
          r.category,
          r.qty,
          r.typeOfReturn,
          `"${(r.returnReason || "").replace(/"/g, '""')}"`,
          `"${(r.detailedReturnReason || "").replace(/"/g, '""')}"`,
          fmtDate(r.returnCreatedDate),
          fmtDate(r.deliveredDate),
          r.courierPartner,
          r.awbNumber,
        ].join(","),
      ),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `meesho-returns-${moment().format("YYYY-MM-DD")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      // Build same filter params used for the list (without page/perPage)
      const params = {};
      if (search) params.search = search;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (selCategory) params.category = selCategory;
      if (selSku) params.sku = selSku;
      if (selReason) params.returnReason = selReason;
      if (isRTO !== "") params.isRTO = isRTO;

      const action = await dispatch(getMeeshoReturnReasonExport(params));
      const rows = action?.payload?.data?.data?.data || [];

      if (!rows.length) {
        enqueueSnackbar("No records found to export.", { variant: "info" });
        return;
      }

      buildAndDownloadCsv(rows);
      enqueueSnackbar(`${rows.length} records exported successfully.`, {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar("Export failed. Please try again.", { variant: "error" });
    } finally {
      setExportLoading(false);
    }
  };

  const summary = analysis.summary || {};
  const rtoPercent =
    summary.total > 0 ? ((summary.rto / summary.total) * 100).toFixed(1) : 0;
  const customerReturnPercent =
    summary.total > 0
      ? ((summary.customerReturns / summary.total) * 100).toFixed(1)
      : 0;

  const pieData = [
    { name: "Customer Return", value: summary.customerReturns || 0 },
    { name: "RTO (Courier)", value: summary.rto || 0 },
  ].filter((d) => d.value > 0);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ pb: 4 }}>
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        gap={1.5}
        mb={2.5}
      >
        <Typography variant="h4" component="h1" fontWeight={700}>
          Meesho — Return Reason Analysis
        </Typography>
        <Stack direction="row" spacing={1} flexShrink={0}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            size="small"
          >
            {uploading ? "Uploading…" : "Import CSV"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            size="small"
          >
            Reset
          </Button>
        </Stack>
      </Stack>

      {uploading && <LinearProgress sx={{ mb: 2 }} />}
      <ImportResultBanner result={importResult} />

      {/* ── Filter Panel ──────────────────────────────────────────────────── */}
      <Card elevation={1} sx={{ mb: 3 }}>
        <CardContent sx={{ pb: "12px !important" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={showFilters ? 2 : 0}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FilterIcon fontSize="small" color="action" />
              <Typography variant="subtitle1" fontWeight={600}>
                Filters
              </Typography>
            </Stack>
            <IconButton size="small" onClick={() => setShowFilters((v) => !v)}>
              {showFilters ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          </Stack>

          <Collapse in={showFilters}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selCategory}
                    label="Category"
                    onChange={(e) => setSelCategory(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {filters.categories.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Return Reason</InputLabel>
                  <Select
                    value={selReason}
                    label="Return Reason"
                    onChange={(e) => setSelReason(e.target.value)}
                  >
                    <MenuItem value="">All Reasons</MenuItem>
                    {filters.reasons.map((r) => (
                      <MenuItem key={r} value={r}>
                        {r}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Return Type</InputLabel>
                  <Select
                    value={isRTO}
                    label="Return Type"
                    onChange={(e) => setIsRTO(e.target.value)}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="false">Customer Return</MenuItem>
                    <MenuItem value="true">RTO (Courier)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  variant="contained"
                  onClick={handleApply}
                  fullWidth
                  size="small"
                  sx={{
                    bgcolor: MEESHO_PINK,
                    "&:hover": { bgcolor: "#c4006b" },
                    whiteSpace: "nowrap",
                  }}
                >
                  Apply
                </Button>
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Analysis" icon={<AnalyticsIcon />} iconPosition="start" />
        <Tab label="Raw Data" icon={<ReturnIcon />} iconPosition="start" />
      </Tabs>

      {/* ══ TAB 0 : ANALYSIS ══════════════════════════════════════════════ */}
      {tab === 0 && (
        <Box>
          {analysis.loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* ── Summary cards ─────────────────────────────────────────── */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  {
                    label: "Total Returns",
                    value: summary.total || 0,
                    sub: `${summary.totalQty || 0} units`,
                    color: MEESHO_PINK,
                    icon: <ReturnIcon fontSize="inherit" />,
                  },
                  {
                    label: "Customer Returns",
                    value: summary.customerReturns || 0,
                    sub: `${customerReturnPercent}% of total`,
                    color: "#D32F2F",
                    icon: <ReturnIcon fontSize="inherit" />,
                  },
                  {
                    label: "RTO (Courier Return)",
                    value: summary.rto || 0,
                    sub: `${rtoPercent}% of total`,
                    color: "#E65100",
                    icon: <RtoIcon fontSize="inherit" />,
                  },
                  {
                    label: "Reason Captured",
                    value: summary.withReason || 0,
                    sub: "customer returns with reason",
                    color: "#2E7D32",
                    icon: <TrendingUpIcon fontSize="inherit" />,
                  },
                ].map((c) => (
                  <Grid item xs={12} sm={6} md={3} key={c.label}>
                    <SummaryCard {...c} />
                  </Grid>
                ))}
              </Grid>

              {/* ── Charts row: bar + pie ──────────────────────────────────── */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Reason breakdown bar */}
                <Grid item xs={12} lg={8}>
                  <Card elevation={2} sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} mb={2}>
                        Return Reason Breakdown
                      </Typography>
                      {analysis.reasonBreakdown.length === 0 ? (
                        <Alert severity="info">
                          No return reason data available. Import a CSV to get
                          started.
                        </Alert>
                      ) : (
                        <ResponsiveContainer
                          width="100%"
                          height={Math.max(
                            200,
                            analysis.reasonBreakdown.length * 52,
                          )}
                        >
                          <BarChart
                            data={analysis.reasonBreakdown}
                            layout="vertical"
                            margin={{ top: 4, right: 56, left: 4, bottom: 4 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              horizontal={false}
                            />
                            <XAxis type="number" tick={{ fontSize: 11 }} />
                            <YAxis
                              type="category"
                              dataKey="reason"
                              width={260}
                              tick={{ fontSize: 12 }}
                              tickLine={false}
                            />
                            <RechartsTooltip
                              formatter={(v, _n, { payload }) => [
                                `${v} (${payload.sharePercent}%)`,
                                "Returns",
                              ]}
                            />
                            <Bar
                              dataKey="count"
                              radius={[0, 4, 4, 0]}
                              label={<BarValueLabel />}
                            >
                              {analysis.reasonBreakdown.map((_, i) => (
                                <Cell
                                  key={i}
                                  fill={CHART_COLORS[i % CHART_COLORS.length]}
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Return type pie */}
                <Grid item xs={12} lg={4}>
                  <Card elevation={2} sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} mb={2}>
                        Return Type Split
                      </Typography>
                      {pieData.length === 0 ? (
                        <Alert severity="info">No data available.</Alert>
                      ) : (
                        <>
                          <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                              <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={58}
                                outerRadius={88}
                                paddingAngle={3}
                                dataKey="value"
                                label={({ percent }) =>
                                  `${(percent * 100).toFixed(1)}%`
                                }
                                labelLine={false}
                              >
                                {pieData.map((_, i) => (
                                  <Cell
                                    key={i}
                                    fill={i === 0 ? MEESHO_PINK : "#FF9800"}
                                  />
                                ))}
                              </Pie>
                              <Legend />
                              <RechartsTooltip
                                formatter={(v, name) => [v, name]}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                          {/* Mini summary below pie */}
                          <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
                            {pieData.map((d, i) => (
                              <Stack
                                key={d.name}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ py: 0.75, px: 0.5 }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: "50%",
                                      flexShrink: 0,
                                      bgcolor:
                                        i === 0 ? MEESHO_PINK : "#FF9800",
                                    }}
                                  />
                                  <Typography variant="body2">
                                    {d.name}
                                  </Typography>
                                </Stack>
                                <Typography variant="body2" fontWeight={700}>
                                  {d.value}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* ── Detailed sub-reasons bar ───────────────────────────────── */}
              {analysis.detailedBreakdown.length > 0 && (
                <Card elevation={2} sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                      Detailed Return Sub-Reasons
                    </Typography>
                    <ResponsiveContainer
                      width="100%"
                      height={Math.max(
                        200,
                        Math.min(15, analysis.detailedBreakdown.length) * 42,
                      )}
                    >
                      <BarChart
                        data={analysis.detailedBreakdown.slice(0, 15)}
                        layout="vertical"
                        margin={{ top: 4, right: 56, left: 4, bottom: 4 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          horizontal={false}
                        />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis
                          type="category"
                          dataKey="detailedReason"
                          width={260}
                          tick={{ fontSize: 11 }}
                          tickLine={false}
                        />
                        <RechartsTooltip
                          formatter={(v, _n, { payload }) => [
                            `${v} (${payload.sharePercent}%)  ·  ${payload.parentReason}`,
                            "Returns",
                          ]}
                        />
                        <Bar
                          dataKey="count"
                          radius={[0, 4, 4, 0]}
                          label={<BarValueLabel />}
                        >
                          {analysis.detailedBreakdown
                            .slice(0, 15)
                            .map((_, i) => (
                              <Cell
                                key={i}
                                fill={CHART_COLORS[i % CHART_COLORS.length]}
                              />
                            ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* ── Daily trend ───────────────────────────────────────────── */}
              {analysis.trend.length > 0 && (
                <Card elevation={2} sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                      Daily Return Trend
                    </Typography>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart
                        data={analysis.trend}
                        margin={{ top: 4, right: 24, left: 0, bottom: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(d) => moment(d).format("DD/MM")}
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <RechartsTooltip
                          labelFormatter={(d) =>
                            moment(d).format("DD MMM YYYY")
                          }
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="total"
                          stroke={MEESHO_PINK}
                          strokeWidth={2}
                          dot={false}
                          name="Total"
                        />
                        <Line
                          type="monotone"
                          dataKey="customerReturn"
                          stroke="#D32F2F"
                          strokeWidth={1.5}
                          dot={false}
                          name="Customer Return"
                          strokeDasharray="5 3"
                        />
                        <Line
                          type="monotone"
                          dataKey="rto"
                          stroke="#FF9800"
                          strokeWidth={1.5}
                          dot={false}
                          name="RTO"
                          strokeDasharray="5 3"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* ── Category Breakdown — full width ───────────────────────── */}
              {analysis.categoryBreakdown.length > 0 && (
                <Card elevation={2} sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                      Category Breakdown
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(MEESHO_PINK, 0.05) }}>
                          <TableCell sx={{ fontWeight: 700, width: "22%" }}>
                            Category
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, width: "9%" }}
                          >
                            Total
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, width: "11%" }}
                          >
                            Customer
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, width: "9%" }}
                          >
                            RTO
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, width: "10%" }}
                          >
                            Cust %
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            Top Return Reason
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analysis.categoryBreakdown.map((c) => {
                          const pct =
                            c.total > 0
                              ? ((c.customerReturn / c.total) * 100).toFixed(0)
                              : 0;
                          return (
                            <TableRow
                              key={c.category}
                              hover
                              sx={{ "&:last-child td": { borderBottom: 0 } }}
                            >
                              <TableCell sx={{ fontWeight: 600, py: 1.25 }}>
                                {c.category}
                              </TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={700} fontSize="0.9rem">
                                  {c.total}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {c.customerReturn}
                              </TableCell>
                              <TableCell align="right">{c.rto}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`${pct}%`}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha("#F44336", 0.1),
                                    color: "#C62828",
                                    fontWeight: 700,
                                    fontSize: "0.75rem",
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <ReasonBadge reason={c.topReason} />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* ── SKU Breakdown — full width ─────────────────────────────── */}
              {analysis.skuBreakdown.length > 0 && (
                <Card elevation={2}>
                  <CardContent>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={2}
                    >
                      <Typography variant="h6" fontWeight={700}>
                        Top Returned SKUs
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click any row to view all reasons
                      </Typography>
                    </Stack>

                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(MEESHO_PINK, 0.05) }}>
                          <TableCell sx={{ width: 36 }} />
                          <TableCell sx={{ fontWeight: 700, width: "18%" }}>
                            SKU
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            Product Name
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, width: "8%" }}
                          >
                            Total
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, width: "9%" }}
                          >
                            Customer
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, width: "7%" }}
                          >
                            RTO
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, width: "30%" }}>
                            Top Return Reason
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analysis.skuBreakdown.map((s) => (
                          <React.Fragment key={s.sku}>
                            <TableRow
                              hover
                              onClick={() => toggleSkuExpand(s.sku)}
                              sx={{
                                cursor: "pointer",
                                bgcolor: expandedSkus.has(s.sku)
                                  ? alpha(MEESHO_PINK, 0.04)
                                  : "transparent",
                                "&:last-child td": expandedSkus.has(s.sku)
                                  ? {}
                                  : { borderBottom: 0 },
                              }}
                            >
                              <TableCell padding="checkbox" sx={{ pl: 1 }}>
                                <IconButton size="small" tabIndex={-1}>
                                  {expandedSkus.has(s.sku) ? (
                                    <CollapseIcon
                                      fontSize="small"
                                      sx={{ color: MEESHO_PINK }}
                                    />
                                  ) : (
                                    <ExpandIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  color={MEESHO_PINK}
                                >
                                  {s.sku}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ wordBreak: "break-word" }}
                                >
                                  {s.productName}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={s.total}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(MEESHO_PINK, 0.1),
                                    color: MEESHO_PINK,
                                    fontWeight: 700,
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                {s.customerReturn}
                              </TableCell>
                              <TableCell align="right">{s.rto}</TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <ReasonBadge reason={s.topReason} />
                              </TableCell>
                            </TableRow>

                            {/* Expanded: all reasons for this SKU */}
                            {expandedSkus.has(s.sku) && (
                              <TableRow>
                                <TableCell
                                  colSpan={7}
                                  sx={{
                                    py: 0,
                                    borderBottom: `2px solid ${alpha(
                                      MEESHO_PINK,
                                      0.15,
                                    )}`,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      px: 3,
                                      py: 2,
                                      bgcolor: alpha(MEESHO_PINK, 0.03),
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      fontWeight={700}
                                      color="text.secondary"
                                      sx={{
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      All return reasons for {s.sku}
                                    </Typography>
                                    <Table size="small" sx={{ mt: 1 }}>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell
                                            sx={{
                                              fontWeight: 600,
                                              color: "text.secondary",
                                              fontSize: "0.75rem",
                                              pl: 0,
                                            }}
                                          >
                                            Return Reason
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{
                                              fontWeight: 600,
                                              color: "text.secondary",
                                              fontSize: "0.75rem",
                                              width: 80,
                                            }}
                                          >
                                            Count
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{
                                              fontWeight: 600,
                                              color: "text.secondary",
                                              fontSize: "0.75rem",
                                              width: 80,
                                            }}
                                          >
                                            Share
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {s.reasons.map((r) => {
                                          const total = s.reasons.reduce(
                                            (a, x) => a + x.count,
                                            0,
                                          );
                                          const pct =
                                            total > 0
                                              ? (
                                                  (r.count / total) *
                                                  100
                                                ).toFixed(0)
                                              : 0;
                                          const { color, bg } = getReasonStyle(
                                            r.reason,
                                          );
                                          return (
                                            <TableRow
                                              key={r.reason}
                                              sx={{
                                                "&:last-child td": {
                                                  borderBottom: 0,
                                                },
                                              }}
                                            >
                                              <TableCell
                                                sx={{ pl: 0, py: 0.75 }}
                                              >
                                                <Box
                                                  sx={{
                                                    display: "inline-block",
                                                    px: 1,
                                                    py: 0.25,
                                                    borderRadius: "4px",
                                                    bgcolor: bg,
                                                    color,
                                                    fontWeight: 600,
                                                    fontSize: "0.78rem",
                                                    wordBreak: "break-word",
                                                  }}
                                                >
                                                  {r.reason}
                                                </Box>
                                              </TableCell>
                                              <TableCell
                                                align="right"
                                                sx={{ fontWeight: 700 }}
                                              >
                                                {r.count}
                                              </TableCell>
                                              <TableCell align="right">
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  {pct}%
                                                </Typography>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Empty state */}
              {!analysis.loading &&
                analysis.reasonBreakdown.length === 0 &&
                analysis.categoryBreakdown.length === 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No data available yet. Use <strong>Import CSV</strong> to
                    upload a Meesho returns export.
                  </Alert>
                )}
            </>
          )}
        </Box>
      )}

      {/* ══ TAB 1 : RAW DATA ══════════════════════════════════════════════ */}
      {tab === 1 && (
        <Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ sm: "center" }}
            mb={2}
            flexWrap="wrap"
          >
            <TextField
              size="small"
              placeholder="Search suborder, SKU, product, AWB…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleApply()}
              sx={{ width: { xs: "100%", sm: 300 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearch("");
                        handleReset();
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={handleApply}
              size="small"
            >
              Search
            </Button>
            <Box flex={1} />
            <Chip
              label={`${list.total} records`}
              size="small"
              variant="outlined"
            />
            <Tooltip title="Export all filtered records as CSV">
              <span>
                <Button
                  variant="outlined"
                  startIcon={
                    exportLoading ? (
                      <CircularProgress size={14} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={handleExportCsv}
                  size="small"
                  disabled={!list.total || exportLoading}
                >
                  {exportLoading ? "Exporting…" : "Export CSV"}
                </Button>
              </span>
            </Tooltip>
          </Stack>

          {list.loading ? (
            <LinearProgress />
          ) : (
            <>
              {/* Scrollable only on the outer container — no nested scrollbars */}
              <Paper elevation={2} sx={{ borderRadius: 2, overflowX: "auto" }}>
                <Table size="small" stickyHeader sx={{ minWidth: 960 }}>
                  <TableHead>
                    <TableRow>
                      {[
                        { label: "Suborder No.", width: 160 },
                        { label: "Product", width: 180 },
                        { label: "SKU", width: 110 },
                        { label: "Size", width: 60 },
                        { label: "Category", width: 100 },
                        { label: "Qty", width: 50 },
                        { label: "Type", width: 90 },
                        { label: "Return Reason", width: 220 },
                        { label: "Detailed Reason", width: 180 },
                        { label: "Return Date", width: 95 },
                        { label: "Delivered", width: 90 },
                        { label: "Courier", width: 100 },
                      ].map(({ label, width }) => (
                        <TableCell
                          key={label}
                          sx={{
                            fontWeight: 700,
                            bgcolor: "background.paper",
                            minWidth: width,
                            fontSize: "0.8rem",
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.data.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                          <ReturnIcon
                            sx={{
                              fontSize: 48,
                              color: "text.disabled",
                              mb: 1,
                              display: "block",
                              mx: "auto",
                            }}
                          />
                          <Typography color="text.secondary">
                            No records found. Import a CSV file to get started.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      list.data.map((row) => (
                        <TableRow
                          key={row._id}
                          hover
                          sx={{ "&:last-child td": { borderBottom: 0 } }}
                        >
                          <TableCell>
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: "monospace", display: "block" }}
                            >
                              {row.suborderNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={row.productName || ""}
                              placement="top-start"
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {row.productName}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color={MEESHO_PINK}
                            >
                              {row.sku}
                            </Typography>
                          </TableCell>
                          <TableCell>{row.variation}</TableCell>
                          <TableCell>{row.category}</TableCell>
                          <TableCell align="center">{row.qty}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.isRTO ? "RTO" : "Customer"}
                              size="small"
                              sx={{
                                bgcolor: row.isRTO
                                  ? alpha("#FF9800", 0.12)
                                  : alpha("#F44336", 0.1),
                                color: row.isRTO ? "#E65100" : "#C62828",
                                fontWeight: 700,
                                fontSize: "0.7rem",
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <ReasonChip reason={row.returnReason} />
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={row.detailedReturnReason || ""}
                              placement="top-start"
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  color: row.detailedReturnReason
                                    ? "text.primary"
                                    : "text.disabled",
                                }}
                              >
                                {row.detailedReturnReason || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              {fmtDate(row.returnCreatedDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              {fmtDate(row.deliveredDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {row.courierPartner}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>

              {/* Pagination */}
              {list.total > 0 && (
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={2}
                  mt={2.5}
                  flexWrap="wrap"
                >
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    ← Previous
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Page <strong>{page}</strong> of{" "}
                    <strong>{Math.ceil(list.total / perPage)}</strong>
                    &nbsp;·&nbsp;{list.total} total
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={page >= Math.ceil(list.total / perPage)}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next →
                  </Button>
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel>Rows / page</InputLabel>
                    <Select
                      value={perPage}
                      label="Rows / page"
                      onChange={handlePerPageChange}
                    >
                      {[10, 25, 100, 500].map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MeeshoReturnReasonAnalysis;
